import { useState, useEffect } from "react";

import { withTranslation } from "react-i18next";
import { Backdrop, Box, CircularProgress, FormControl, InputAdornment, TextField, Typography } from "@mui/material";
import AppCard from "@plannel/components/ui/AppCard";
import CardContent from "@mui/material/CardContent";
import { AttachMoney, TrendingUp, Leaderboard } from '@mui/icons-material';
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import Snackbar from "@plannel/components/Snackbar"
import Dialog from "@plannel/components/Dialog";
import planConfigService from "@plannel/services/data/plan-config-service";
import { size, includes, isEmpty } from "lodash";
import { FS } from "@plannel/utils/font-util";

const DpAlertSettings = (props) => {
  const { t, close, open, reload, userRoles } = props;

  const { snackBarMessageColor: { saveSuccess, saveFail } } = ColorUtil;
  const { roles } = CodeUtil; 

  const [planConfig, setPlanConfig] = useState({});
  const [salesUpperThreshold, setSalesUpperThreshold] = useState(0);
  const [forecastUpperThreshold, setForecastUpperThreshold] = useState(0);
  const [demandUpperThreshold, setDemandUpperThreshold] = useState(0);
  const [salesLowerThreshold, setSalesLowerThreshold] = useState(0);
  const [forecastLowerThreshold, setForecastLowerThreshold] = useState(0);
  const [demandLowerThreshold, setDemandLowerThreshold] = useState(0);
  const [trendMovingAverage, setTrendMovingAverage] = useState(3);

  const [finishCreate, setFinishCreate] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const handleClose = () => {
    close(false);
  };

  const retrievePlanConfig = () => {
    const param = {
      moduleCd: "DP",
      activeFlg: true,
      configCd: "ERROR_THRESHOLD"
    };

    planConfigService.getConfigCd(param)
      .then((response) => {
        if (response?.data) {
          setPlanConfig(response.data);
        }
        if (response?.data?.objVal) {
          const { salesTrendUpper, salesTrendLower, forecastUpper, forecastLower, demandLower, demandUpper, movingAverage } = response.data.objVal;
          setSalesUpperThreshold(Number(salesTrendUpper? salesTrendUpper : 0));
          setForecastUpperThreshold(Number(forecastUpper? forecastUpper : 0));
          setDemandUpperThreshold(Number(demandUpper? demandUpper : 0));
          setSalesLowerThreshold(Number(salesTrendLower? salesTrendLower : 0));
          setForecastLowerThreshold(Number(forecastLower? forecastLower : 0));
          setDemandLowerThreshold(Number(demandLower? demandLower : 0));
          setTrendMovingAverage(Number(movingAverage? movingAverage : 3));
        }
      }).catch(e => {
        debug(e);
      })
  };

  const handleSave = () => {
    setFinishCreate(true);
    const params = [];
    if (size(planConfig) > 0) {
      planConfig.objVal = {
        salesTrendUpper: salesUpperThreshold,
        forecastUpper: forecastUpperThreshold ?? 0,
        demandUpper: demandUpperThreshold,
        salesTrendLower: salesLowerThreshold,
        forecastLower: forecastLowerThreshold ?? 0,
        demandLower: demandLowerThreshold,
        movingAverage: trendMovingAverage
      }
      params.push({...planConfig});
    } else {
      params.push({
        moduleCd: "DP",
        configCd: "ERROR_THRESHOLD",
        activeFlg: true,
        objVal: {
          salesTrendUpper: salesUpperThreshold,
          forecastUpper: isEmpty(forecastUpperThreshold) ? 0 : forecastUpperThreshold,
          demandUpper: demandUpperThreshold,
          salesTrendLower: salesLowerThreshold,
          forecastLower:  isEmpty(forecastLowerThreshold) ? 0 : forecastLowerThreshold,
          demandLower: demandLowerThreshold,
          movingAverage: trendMovingAverage
        }
      });
    }

    planConfigService.upsert(params)
      .then(() => {
        setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
        reload(true);
        close(true);
        setFinishCreate(false);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        setFinishCreate(false);
      })
    return true;
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      handleSave();
      reload(true);
    }
  };

  useEffect(() => {
    if (open) {
      retrievePlanConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      <Backdrop sx={{ display: "flex", color: '#fff', zIndex: 10000 }} open={finishCreate} >
        <CircularProgress color="inherit" />
      </Backdrop>
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={t("dpAlertSettings")}
        onSave={handleSave}
        width={532}
      >
          <AppCard variant="outlined" sx={{ mb: 1 }}>
            <CardContent sx={{ pb: 0 }} >
              <Box sx={{ display: "flex", width:"100%" }}>
                <Leaderboard fontSize={"medium"} sx={{ mb: 0.2, mr: 1 }} />
                <Typography id="modal-modal-title" variant="subtitle1" sx={{width: "45%"}} >
                  {t("salesTrendThreshold")}
                </Typography>
                <Box sx={{ display: "flex", width: "50%", alignItems: "center" }}>
                  <Typography sx={{ fontSize: FS.CAPTION, mr: 1 }} >
                    {t("movingAverage")}
                  </Typography>
                  <FormControl size="small" style={{ width: "20%" }} >
                    <TextField
                      size="small"
                      value={trendMovingAverage}
                      type="number"
                      variant="standard"
                      InputProps={{ inputMode: 'numeric', pattern: '[0-9]', inputProps: { min: 1 }, style: { fontSize: FS.CAPTION } }}
                      onChange={(event) => setTrendMovingAverage(event.target.value)}
                    />
                  </FormControl>
                </Box>
              </Box>
              <Box size="small" sx={{ mt: 1, gap: 1, display: "flex", textAlign: "center" }} >
              <FormControl size="small" style={{ width: "100%" }} >
                  <TextField
                    label={t("salesTrendLowerThreshold")}
                    size="small"
                    value={salesLowerThreshold}
                    type="number"
                    InputProps={{ inputMode: 'numeric', pattern: '[0-9]', endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    onChange={(event) => setSalesLowerThreshold(event.target.value)}
                  />
                </FormControl>
                <FormControl size="small" style={{ width: "100%" }} >
                  <TextField
                    label={t("salesTrendUpperThreshold")}
                    size="small"
                    value={salesUpperThreshold}
                    type="number"
                    InputProps={{ inputMode: 'numeric', pattern: '[0-9]', endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    onChange={(event) => setSalesUpperThreshold(event.target.value)}
                  />
                </FormControl>
              </Box>
            </CardContent>
            {includes(userRoles, roles.APP_BF) ? 
              <CardContent sx={{ pb: 0 }} >
                <Box sx={{ display: "flex" }}>
                  <TrendingUp fontSize={"medium"} sx={{ mb: 0.2, mr: 1 }} />
                  <Typography id="modal-modal-title" variant="subtitle1" >
                    {t("forecastErrorThreshold")}
                  </Typography>
                </Box>
                <Box size="small" sx={{ mt: 1, gap: 1, display: "flex", textAlign: "center" }} >
                <FormControl size="small" style={{ width: "100%" }} >
                    <TextField
                      label={t("forecastErrorLowerThreshold")}
                      size="small"
                      value={forecastLowerThreshold}
                      type="number"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      onChange={(event) => setForecastLowerThreshold(event.target.value)}
                    />
                  </FormControl>
                  <FormControl size="small" style={{ width: "100%" }} >
                    <TextField
                      label={t("forecastErrorUpperThreshold")}
                      size="small"
                      value={forecastUpperThreshold}
                      type="number"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      onChange={(event) => setForecastUpperThreshold(event.target.value)}
                    />
                  </FormControl>
                </Box>
              </CardContent>
            : null}
            <CardContent sx={{ pb: 0 }} >
              <Box sx={{ display: "flex" }}>
                <AttachMoney fontSize={"medium"} sx={{ mb: 0.2, mr: 1 }} />
                <Typography id="modal-modal-title" variant="subtitle1" >
                  {t("demandPlanThreshold")}
                </Typography>
              </Box>
              <Box size="small" sx={{ mt: 1, gap: 1, display: "flex", textAlign: "center" }} >
              <FormControl size="small" style={{ width: "100%" }} >
                  <TextField
                    label={t("demandPlanLowerThreshold")}
                    size="small"
                    value={demandLowerThreshold}
                    type="number"
                    InputProps={{ inputMode: 'numeric', pattern: '[0-9]', endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    onChange={(event) => setDemandLowerThreshold(event.target.value)}
                  />
                </FormControl>
                <FormControl size="small" style={{ width: "100%" }} >
                  <TextField
                    label={t("demandPlanUpperThreshold")}
                    size="small"
                    value={demandUpperThreshold}
                    type="number"
                    InputProps={{ inputMode: 'numeric', pattern: '[0-9]', endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    onChange={(event) => setDemandUpperThreshold(event.target.value)}
                  />
                </FormControl>
              </Box>
            </CardContent>
          </AppCard>
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </>
  );
};

export default withTranslation()(DpAlertSettings);